import { useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  BookOpen,
  Scale,
  Info,
  FileText,
  FolderOpen,
  LogIn,
  Lock,
  X,
  Loader2,
} from "lucide-react";
import { clsx } from "clsx";
import { lookupPasalRefs, type PasalData } from "@/data/uuPnbpPasal";
import { useAuthStore } from "@/store/authSlice";
import { api } from "@/api/client";
import {
  PNBP_TREE,
  type TreeSubItem,
  type TreeCategory,
} from "@/data/pnbpTree";

// ── Type untuk jenis PNBP dari DB ─────────────────────────────────────────────
type JenisPnbpItem = { id: string; kode: string; nama: string; urutan: number };

// ─── Color map ────────────────────────────────────────────────────────────────

const COLOR_MAP = {
  blue: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    badge: "bg-blue-100 text-blue-700",
    icon: "bg-blue-600",
    num: "text-blue-600",
  },
  indigo: {
    bg: "bg-indigo-50",
    border: "border-indigo-200",
    badge: "bg-indigo-100 text-indigo-700",
    icon: "bg-indigo-600",
    num: "text-indigo-600",
  },
  violet: {
    bg: "bg-violet-50",
    border: "border-violet-200",
    badge: "bg-violet-100 text-violet-700",
    icon: "bg-violet-600",
    num: "text-violet-600",
  },
  emerald: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    badge: "bg-emerald-100 text-emerald-700",
    icon: "bg-emerald-600",
    num: "text-emerald-600",
  },
  amber: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    badge: "bg-amber-100 text-amber-700",
    icon: "bg-amber-500",
    num: "text-amber-600",
  },
  rose: {
    bg: "bg-rose-50",
    border: "border-rose-200",
    badge: "bg-rose-100 text-rose-700",
    icon: "bg-rose-600",
    num: "text-rose-600",
  },
  cyan: {
    bg: "bg-cyan-50",
    border: "border-cyan-200",
    badge: "bg-cyan-100 text-cyan-700",
    icon: "bg-cyan-600",
    num: "text-cyan-600",
  },
  orange: {
    bg: "bg-orange-50",
    border: "border-orange-200",
    badge: "bg-orange-100 text-orange-700",
    icon: "bg-orange-500",
    num: "text-orange-600",
  },
  red: {
    bg: "bg-red-50",
    border: "border-red-200",
    badge: "bg-red-100 text-red-700",
    icon: "bg-red-600",
    num: "text-red-600",
  },
  teal: {
    bg: "bg-teal-50",
    border: "border-teal-200",
    badge: "bg-teal-100 text-teal-700",
    icon: "bg-teal-600",
    num: "text-teal-600",
  },
};

export type ColorMapKey = keyof typeof COLOR_MAP;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractLabelNumber(label: string): number | null {
  const m = label.trim().match(/^(\d+)\.$/);
  return m ? parseInt(m[1]) : null;
}

// ─── Pasal Detail Modal ───────────────────────────────────────────────────────

export function PasalModal({
  pasals,
  subItemName,
  onClose,
}: {
  pasals: PasalData[];
  subItemName: string;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-start gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50/80 flex-shrink-0">
          <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Scale className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold text-gray-900 leading-tight">
              {subItemName}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {pasals.map((p) => p.referensi).join(" · ")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200 flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-5 space-y-6">
          {pasals.map((pasal) => (
            <div key={pasal.key}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wide">
                  {pasal.bab}
                </span>
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">
                {pasal.referensi}
              </h3>
              <p className="text-sm font-medium text-gray-600 mb-3">
                {pasal.judul}
              </p>
              {pasal.pembuka && (
                <p className="text-sm text-gray-700 leading-relaxed mb-3 italic">
                  {pasal.pembuka}
                </p>
              )}
              <div className="space-y-3">
                {pasal.ayat.map((ayat, idx) => {
                  const isShortLabel =
                    !ayat.label ||
                    /^(\d+\.|[a-zA-Z]\.|[a-zA-Z]\)|\([0-9a-zA-Z]+\))$/.test(
                      ayat.label.trim(),
                    );
                  const prevLabel = idx > 0 ? pasal.ayat[idx - 1].label : null;
                  const currNum = extractLabelNumber(ayat.label);
                  const prevNum = prevLabel
                    ? extractLabelNumber(prevLabel)
                    : null;
                  const hasGap =
                    currNum !== null &&
                    prevNum !== null &&
                    currNum > prevNum + 1;
                  return (
                    <div key={idx}>
                      {hasGap && (
                        <div className="flex gap-3 items-center py-1">
                          <span className="text-gray-300 flex-shrink-0 w-10 text-right text-base leading-none select-none">
                            ⋮
                          </span>
                          <span className="text-xs text-gray-400 italic">
                            angka {prevNum + 1}–{currNum - 1} tidak ditampilkan
                          </span>
                        </div>
                      )}
                      {isShortLabel ? (
                        <div className="flex gap-3">
                          {ayat.label && (
                            <span className="text-sm font-bold text-gray-400 flex-shrink-0 w-10 text-right mt-0.5">
                              {ayat.label}
                            </span>
                          )}
                          <div className="flex-1">
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {ayat.teks}
                            </p>
                            {ayat.sub && (
                              <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-gray-200">
                                {ayat.sub.map((s, si) => (
                                  <div key={si} className="flex gap-2">
                                    <span className="text-xs font-bold text-gray-400 flex-shrink-0 w-6 text-right mt-0.5">
                                      {s.label}
                                    </span>
                                    <p className="text-sm text-gray-600 leading-relaxed">
                                      {s.teks}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <span className="inline-flex items-center mb-1.5 px-2 py-0.5 rounded-md bg-blue-50 text-xs font-semibold text-blue-700">
                            {ayat.label}
                          </span>
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {ayat.teks}
                          </p>
                          {ayat.sub && (
                            <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-gray-200">
                              {ayat.sub.map((s, si) => (
                                <div key={si} className="flex gap-2">
                                  <span className="text-xs font-bold text-gray-400 flex-shrink-0 w-6 text-right mt-0.5">
                                    {s.label}
                                  </span>
                                  <p className="text-sm text-gray-600 leading-relaxed">
                                    {s.teks}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {pasal.catatan && (
                <div className="mt-4 flex gap-2.5 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
                  <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <span className="font-semibold">Penjelasan UU: </span>
                    {pasal.catatan}
                  </p>
                </div>
              )}
              {pasals.length > 1 && <hr className="mt-6 border-gray-100" />}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/60 flex-shrink-0">
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <FileText className="h-3.5 w-3.5" />
            <span>LN 2018 No. 147 · TLN No. 6245 · 23 Agustus 2018</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-item ─────────────────────────────────────────────────────────────────

export function SubItem({
  item,
  color,
  onPasalClick,
}: {
  item: TreeSubItem;
  color: ColorMapKey;
  onPasalClick: (item: TreeSubItem, pasals: PasalData[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [showLoginGate, setShowLoginGate] = useState(false);
  const c = COLOR_MAP[color];
  const hasChildren =
    (item.children?.length ?? 0) > 0 || !!item.jenisPnbpChildren;
  const pasals = useMemo(
    () => (item.pasal ? lookupPasalRefs(item.pasal) : []),
    [item.pasal],
  );
  const hasPasalContent = pasals.length > 0;
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();

  // Fetch jenis PNBP dari DB hanya jika flag aktif dan item dibuka
  const { data: jenisList = [], isLoading: jenisPnbpLoading } = useQuery<
    JenisPnbpItem[]
  >({
    queryKey: ["public", "jenis-pnbp"],
    queryFn: () => api.get<JenisPnbpItem[]>("/public/jenis-pnbp"),
    enabled: !!item.jenisPnbpChildren && open,
    staleTime: 10 * 60 * 1000,
  });

  const handleDocLink = () => {
    if (!item.docLink) return;
    if (isAuthenticated) {
      navigate(item.docLink);
    } else {
      setShowLoginGate((v) => !v);
    }
  };

  return (
    <div className={clsx("rounded-lg border overflow-hidden", c.border)}>
      <div className="flex items-start gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => hasChildren && setOpen((v) => !v)}
          className={clsx(
            "flex-1 flex items-start gap-2 text-left",
            hasChildren ? "cursor-pointer" : "cursor-default",
          )}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-800">
                {item.name}
              </span>
              {item.pasal?.map((p) => (
                <span
                  key={p}
                  className={clsx(
                    "text-xs font-semibold px-2 py-0.5 rounded-full",
                    c.badge,
                  )}
                >
                  {p}
                </span>
              ))}
            </div>
            {item.description && (
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                {item.description}
              </p>
            )}
          </div>
          {hasChildren && (
            <span className="text-gray-400 mt-0.5 flex-shrink-0">
              {open ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </span>
          )}
        </button>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {item.pnbpKategoriLink && (
            <Link
              to={item.pnbpKategoriLink}
              title="Lihat Objek PNBP"
              className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
            >
              <FolderOpen className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Objek PNBP</span>
            </Link>
          )}
          {item.docLink && (
            <button
              type="button"
              onClick={handleDocLink}
              title="Lihat Struktur Dokumen"
              className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200"
            >
              <FolderOpen className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Struktur Dokumen</span>
            </button>
          )}
          {hasPasalContent && (
            <button
              type="button"
              onClick={() => onPasalClick(item, pasals)}
              title={`Baca teks UU: ${item.pasal?.join(", ")}`}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200"
            >
              <BookOpen className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Baca</span>
            </button>
          )}
        </div>
      </div>

      {showLoginGate && !isAuthenticated && item.docLink && (
        <div className="border-t border-violet-200 bg-violet-50 px-4 py-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-violet-600">
              <Lock className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-violet-900">
                Akses Diperlukan
              </p>
              <p className="text-xs text-violet-700 mt-0.5 leading-relaxed">
                Halaman <strong>Struktur Dokumen</strong> hanya dapat diakses
                oleh pengguna yang telah masuk.
              </p>
              <div className="flex items-center gap-2 mt-3">
                <Link
                  to={`/login?redirect=${encodeURIComponent(item.docLink)}`}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 transition-colors"
                >
                  <LogIn className="h-3.5 w-3.5" />
                  Masuk Sekarang
                </Link>
                <button
                  type="button"
                  onClick={() => setShowLoginGate(false)}
                  className="text-xs text-violet-500 hover:text-violet-700"
                >
                  Batal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {open && hasChildren && (
        <div className={clsx("border-t", c.border, c.bg, "px-4 py-3")}>
          {item.jenisPnbpChildren ? (
            /* ── Children dinamis dari DB ── */
            jenisPnbpLoading ? (
              <div className="flex items-center gap-2 py-1 text-xs text-gray-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Memuat…
              </div>
            ) : (
              <ul className="space-y-1.5">
                {jenisList.map((jenis) => (
                  <li key={jenis.id}>
                    <Link
                      to={`/pnbp-objek?nama=${encodeURIComponent(jenis.nama)}`}
                      className={clsx(
                        "flex items-center gap-2 rounded-md px-2 py-1 text-sm text-gray-700 hover:text-violet-700 hover:bg-violet-50 transition-colors group",
                      )}
                    >
                      <span
                        className={clsx(
                          "h-1.5 w-1.5 rounded-full flex-shrink-0 group-hover:bg-violet-500",
                          c.icon,
                        )}
                      />
                      {jenis.nama}
                    </Link>
                  </li>
                ))}
              </ul>
            )
          ) : (
            /* ── Children statis ── */
            <ul className="space-y-2">
              {item.children?.map((child, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span
                    className={clsx(
                      "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                      c.icon,
                    )}
                  />
                  <div>
                    <span className="text-sm text-gray-700">{child.name}</span>
                    {child.description && (
                      <p className="text-xs text-gray-500 leading-relaxed">
                        {child.description}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Category card ────────────────────────────────────────────────────────────

export function CategoryCard({
  category,
  forceOpen,
  onPasalClick,
}: {
  category: TreeCategory;
  forceOpen: boolean | null;
  onPasalClick: (item: TreeSubItem, pasals: PasalData[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const c = COLOR_MAP[category.color];
  const isOpen = forceOpen !== null ? forceOpen : open;
  const totalItems = category.items.reduce(
    (acc, item) => acc + 1 + (item.children?.length ?? 0),
    0,
  );

  return (
    <div className={clsx("rounded-xl border-2 overflow-hidden", c.border)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div
          className={clsx(
            "h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0",
            c.icon,
          )}
        >
          <span className="text-sm font-bold text-white">{category.id}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-bold text-gray-900">
              {category.title}
            </h3>
            {category.subtitle && (
              <span
                className={clsx(
                  "text-xs font-semibold px-2 py-0.5 rounded-full",
                  c.badge,
                )}
              >
                {category.subtitle}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {category.items.length} sub-topik &middot; {totalItems} item
          </p>
        </div>
        <span className="text-gray-400 flex-shrink-0">
          {isOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </span>
      </button>

      {isOpen && (
        <div className={clsx("border-t-2", c.border, "p-5 space-y-3")}>
          {category.items.map((item, idx) => (
            <SubItem
              key={idx}
              item={item}
              color={category.color}
              onPasalClick={onPasalClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Full tree view (reusable) ────────────────────────────────────────────────

export default function StrukturPnbpTree({
  forceOpen = null,
  excludeIds = [],
}: {
  forceOpen?: boolean | null;
  excludeIds?: number[];
}) {
  const [modal, setModal] = useState<{
    item: TreeSubItem;
    pasals: PasalData[];
  } | null>(null);

  const handlePasalClick = useCallback(
    (item: TreeSubItem, pasals: PasalData[]) => {
      setModal({ item, pasals });
    },
    [],
  );

  return (
    <>
      {modal && (
        <PasalModal
          pasals={modal.pasals}
          subItemName={modal.item.name}
          onClose={() => setModal(null)}
        />
      )}
      <div className="space-y-3">
        {PNBP_TREE.filter((cat) => !excludeIds.includes(cat.id)).map((cat) => (
          <CategoryCard
            key={cat.id}
            category={cat}
            forceOpen={forceOpen}
            onPasalClick={handlePasalClick}
          />
        ))}
      </div>
    </>
  );
}
